/** drawingName.ts — parse a drawing filename into number / title / revision.
 *
 *  One canonical filename heuristic, shared by the import (to populate
 *  `document_title`) and the gallery/uploader display, so a sheet's parsed
 *  title is identical wherever it's shown. Prefers an explicit "<number> -
 *  <title>" separator, else peels a leading drawing-number token; the tender/
 *  issue revision suffix (T1 / P2 / C3 / lettered) is split off the number.
 *  Underscores become spaces. Extraction only — never guesses a title.
 */

export interface ParsedDrawingName {
  /** Drawing number, e.g. "1977-AE-109" (revision suffix removed). */
  number: string;
  /** Descriptive title, e.g. "Proposed Front Elev A". Empty when the filename
   *  is just a number with no descriptive part. */
  title: string;
  /** Revision code peeled off the number, e.g. "T1". Empty when none. */
  revision: string;
}

export function parseDrawingName(name: string): ParsedDrawingName {
  const base = name.replace(/\.pdf$/i, '');

  let numberRaw = '';
  let title = '';
  const explicit = base.match(/^(.*?)[ _]-[ _](.+)$/);
  if (explicit) {
    numberRaw = explicit[1];
    title = explicit[2];
  } else {
    // Peel a leading drawing-number token (letters?+digits then hyphen-segments,
    // e.g. "1977-AE-109" / "J4045-1400-T1").
    const prefix = base.match(/^([A-Z]{0,4}\d{2,5}(?:-[A-Z0-9]{1,5})+)[ _]+(.+)$/i);
    if (prefix) {
      numberRaw = prefix[1];
      title = prefix[2];
    } else {
      title = base;
    }
  }
  numberRaw = numberRaw.replace(/_/g, ' ').trim();
  title = title.replace(/_/g, ' ').trim();

  // Tender/issue revision suffix on the number, e.g. "J4045-1000-T1" -> number
  // "J4045-1000", rev "T1". Covers tender (T), preliminary (P), construction
  // (C) and simple lettered revs.
  let number = numberRaw;
  let revision = '';
  const rm = numberRaw.match(/[-\s]([TPCA]\d{1,3})$/i);
  if (rm && rm.index !== undefined) {
    revision = rm[1].toUpperCase();
    number = numberRaw.slice(0, rm.index).replace(/[-\s]+$/, '').trim();
  }

  return { number, title, revision };
}
